import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Link from "redux-first-router-link";
import { utc, tz } from "moment-timezone";
import PropTypes from "prop-types";

import {
  Divider,
  Accordion,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";

import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";

import { fullWidthStyles } from "./fullWidthStyles";

import SimpleTable from "../SimpleTable";

import "./Detail.sass";

const Evidence = ({ evidence }) => {
  const { matches = [] } = evidence;

  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    // if no data provided, no need to update
    if (matches.length) {
      const formattedData = formatData(matches);
      setData(formattedData);
      const formattedColumns = formatColumns(Object.keys(formattedData[0]));
      setColumns(formattedColumns);
    }
  }, [evidence]);

  // format data to how our table likes it
  // happy table = happy life

  const formatColumns = (uniqueKeys) =>
    uniqueKeys.map((uniqueKey) => {
      return { title: uniqueKey, field: `${uniqueKey}`, searchable: true };
    });

  const isObject = (value) => {
    return typeof value === "object";
  };

  const parseValue = (value) => {
    const valueToReturn = JSON.parse(value);
    if (isObject(valueToReturn)) {
      if (Array.isArray(valueToReturn)) return valueToReturn.join(", ");
      return JSON.stringify(valueToReturn, null, 2);
    }
    return valueToReturn;
  };

  const formatIndividualMatch = (match, index) => {
    // id is for search capability
    let matchToReturn = { id: `${index}` };
    match.forEach((el) => (matchToReturn[el.key] = parseValue(el.value)));
    return matchToReturn;
  };

  const formatData = (matches) =>
    matches.map((match, i) => formatIndividualMatch(match, i));

  return <SimpleTable isNorthStar data={data} columns={columns} />;
};

Evidence.propTypes = {
  evidence: PropTypes.shape({}),
};

Evidence.defaultProps = {
  evidence: [],
};

const FindingDetailView = ({
  srcCountry = [],
  summary = [],
  evidence = [],
  relatedFindings = [],
  statusModified,
}) => {
  const classes = fullWidthStyles();

  return (
    <Accordion>
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<KeyboardArrowDownIcon />}
      >
        <Typography className={classes.accordionTitle}>Details</Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionPanel}>
        <div className={classes.findingsDetailContainer}>
          {statusModified && (
            <div className={classes.sectionContainer}>
              <span className={classes.sectionTitle}>Status Modified</span>
              {utc(statusModified).tz(tz.guess(true)).format("lll z")}
            </div>
          )}

          {summary && (
            <div className={classes.sectionContainer}>
              <span className={classes.sectionTitle}>Summary</span>
              {summary}
            </div>
          )}

          {srcCountry.length > 0 && (
            <div className={classes.sectionContainer}>
              <span className={classes.sectionTitle}>Source Countries</span>
              <ul>
                {srcCountry.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          {evidence.length > 0 && (
            <div className={classes.sectionContainer}>
              <span className={classes.sectionTitle}>Matched Evidence</span>
              <div>
                {evidence.map((x) => (
                  <div key={x.timeMatched} className="evidence">
                    <div className={classes.matchedMeta}>
                      <span>
                        <b>Time Matched</b>
                      </span>
                      <span className={classes.matchedMetaText}>
                        {utc(x.timeMatched).tz(tz.guess(true)).format("lll z")}
                      </span>
                      <span className={classes.matchedMetaText}>|</span>
                      <span className={classes.matchedMetaText}>
                        <b>Matches</b>
                      </span>
                      <span className={classes.matchedMetaText}>
                        {`${x.matches.length}`}
                      </span>
                    </div>
                    <Evidence evidence={x} />
                    <Divider className={classes.divider} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {relatedFindings.length > 0 && (
            <div className={classes.sectionContainer}>
              <span className={classes.sectionTitle}>Related Findings</span>
              <div>
                {relatedFindings.map((finding) => (
                  <div key={finding.id} className="relatedFinding">
                    <Link to={finding.linkTo}>{finding.name}</Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

FindingDetailView.propTypes = {
  evidence: PropTypes.arrayOf(PropTypes.shape({})),
  srcCountry: PropTypes.arrayOf(PropTypes.string),
  summary: PropTypes.string,
  statusModified: PropTypes.string,
  relatedFindings: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      linkTo: PropTypes.shape({}),
    })
  ),
};

FindingDetailView.defaultProps = {
  evidence: [],
  srcCountry: [],
  summary: "",
  statusModified: "",
  relatedFindings: [],
};

const mapStatetoProps = ({ findings }, { status, relatedFindings }) => ({
  status: findings.get("constants").status.fromId(status),
  relatedFindings: findings
    .get("findings")
    .filter((i) => relatedFindings.indexOf(i.id) > -1)
    .map((finding) => ({
      name: finding.name,
      id: finding.id,
      linkTo: {
        type: "PAGE",
        payload: {
          orgId: finding.orgId,
          toplevel: "reporting",
          secondlevel: "findings",
          id1: finding.id,
        },
      },
    })),
});

export default connect(mapStatetoProps)(FindingDetailView);
